import React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const fmtEur = (value: number): string =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(value);

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** e.g. "Lastschriften" or "Rückerstattungen". */
  itemLabel: string;
  count: number;
  totalAmount: number;
  isPending: boolean;
  onConfirm: () => void;
  /** Relabel the dialog for re-exporting previously returned transactions. */
  reexportMode?: boolean;
}

/**
 * Confirmation before creating a SEPA export batch. Summarises how many
 * transactions and what total will be written into the SEPA file.
 */
const ExportConfirmDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  itemLabel,
  count,
  totalAmount,
  isPending,
  onConfirm,
  reexportMode,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl">
            {reexportMode ? "SEPA-Export erneut erstellen" : "SEPA-Export erstellen"}
          </DialogTitle>
          <DialogDescription className="text-[15px]">
            Es wird eine SEPA-Datei erstellt und die ausgewählten Transaktionen als exportiert
            markiert.
          </DialogDescription>
        </DialogHeader>

        <div className="my-2 grid grid-cols-2 gap-4 rounded-lg border border-solid border-slate-200 bg-slate-50/60 p-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Anzahl</p>
            <p className="mt-0.5 text-lg font-semibold text-slate-800">
              {count} {itemLabel}
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Summe</p>
            <p className="mt-0.5 text-lg font-semibold text-slate-800">{fmtEur(totalAmount)}</p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            className="text-base"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Abbrechen
          </Button>
          <Button
            className="bg-blue-darkest text-base text-white hover:bg-blue-darkest/90"
            onClick={onConfirm}
            disabled={isPending || count === 0}
          >
            {isPending ? "Wird exportiert…" : reexportMode ? "Erneut exportieren" : "Exportieren"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExportConfirmDialog;
